// src/components/ChatWidget.jsx
// Floating "Need Help?" chat assistant grounded in PADD/CDBG knowledge.

import { useState, useRef, useEffect } from 'react';

const C = {
  navy: '#122326', slate: '#213A3E', gold: '#BF6847',
  cream: '#F0EFEB', sand: '#E2E0DB', white: '#FFFFFF',
};

const GREETING = `Hi! I'm the PADD Assistant. I can help you with:

• Finding Davis-Bacon wage determinations
• Understanding CDBG national objectives and LMI requirements
• Navigating environmental review (24 CFR 58)
• Identifying which grants your project may qualify for
• Connecting you with PADD staff

What can I help you with today?`;

// Render text with clickable URLs and line breaks
function MessageText({ text }) {
  const urlRegex = /(https?:\/\/[^\s<>"]+)/g;
  const lines = text.split('\n');

  return (
    <>
      {lines.map((line, li) => {
        const tokens = line.split(urlRegex);
        return (
          <span key={li}>
            {tokens.map((token, ti) =>
              urlRegex.test(token) ? (
                <a
                  key={ti}
                  href={token}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: C.gold, wordBreak: 'break-all' }}
                >
                  {token}
                </a>
              ) : (
                <span key={ti}>{token}</span>
              )
            )}
            {li < lines.length - 1 && <br />}
          </span>
        );
      })}
    </>
  );
}

// Three-dot typing indicator
function TypingDots() {
  return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', height: 18 }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: '#aaa',
            animation: `chatDotBounce 1.2s ease-in-out ${i * 0.2}s infinite`,
          }}
        />
      ))}
      <style>{`
        @keyframes chatDotBounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
          40% { transform: translateY(-5px); opacity: 1; }
        }
      `}</style>
    </span>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: GREETING },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to latest message
  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  async function send() {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);

    try {
      // Send only role+content pairs to the API (strip any UI-only fields)
      const apiMessages = nextMessages.map(({ role, content }) => ({ role, content }));

      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages }),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: 'Sorry, something went wrong. Please try again or contact PADD directly at kylek@nepadd.com.' },
        ]);
      } else {
        setMessages((prev) => [...prev, { role: 'assistant', content: data.content }]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Unable to reach the assistant right now. You can reach PADD staff at kylek@nepadd.com or 308-632-4957.' },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close chat' : 'Open help chat'}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          backgroundColor: C.navy,
          color: C.white,
          border: `2px solid ${C.gold}`,
          borderRadius: 28,
          padding: '10px 20px',
          fontSize: 14,
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
          transition: 'transform 0.15s, box-shadow 0.15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.32)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.25)';
        }}
      >
        <span style={{ fontSize: 18 }}>{open ? '✕' : '💬'}</span>
        {open ? 'Close' : 'Need Help?'}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          style={{
            position: 'fixed',
            bottom: 80,
            right: 24,
            zIndex: 9999,
            width: 360,
            maxWidth: 'calc(100vw - 48px)',
            height: 480,
            maxHeight: 'calc(100vh - 120px)',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: C.white,
            borderRadius: 14,
            border: `1px solid ${C.sand}`,
            boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
            overflow: 'hidden',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          }}
        >
          {/* Header */}
          <div
            style={{
              backgroundColor: C.navy,
              padding: '14px 18px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                backgroundColor: C.gold,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                flexShrink: 0,
              }}
            >
              🌾
            </div>
            <div>
              <div style={{ color: C.white, fontWeight: 700, fontSize: 14 }}>PADD Assistant</div>
              <div style={{ color: '#8aacb0', fontSize: 11 }}>CDBG &amp; Funding Guidance</div>
            </div>
          </div>

          {/* Message list */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '14px 14px 0',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '85%',
                    padding: '9px 13px',
                    borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                    backgroundColor: msg.role === 'user' ? C.gold : C.cream,
                    color: msg.role === 'user' ? C.white : C.navy,
                    fontSize: 13,
                    lineHeight: 1.55,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  <MessageText text={msg.content} />
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div
                  style={{
                    padding: '10px 14px',
                    borderRadius: '14px 14px 14px 4px',
                    backgroundColor: C.cream,
                  }}
                >
                  <TypingDots />
                </div>
              </div>
            )}

            <div ref={bottomRef} style={{ height: 8 }} />
          </div>

          {/* Input row */}
          <div
            style={{
              padding: '10px 12px',
              borderTop: `1px solid ${C.sand}`,
              display: 'flex',
              gap: 8,
              flexShrink: 0,
              backgroundColor: C.white,
            }}
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask about Davis-Bacon, LMI data, environmental review…"
              rows={1}
              style={{
                flex: 1,
                resize: 'none',
                border: `1px solid ${C.sand}`,
                borderRadius: 8,
                padding: '8px 10px',
                fontSize: 13,
                lineHeight: 1.4,
                fontFamily: 'inherit',
                outline: 'none',
                backgroundColor: C.white,
                color: C.navy,
                maxHeight: 80,
                overflowY: 'auto',
              }}
              onFocus={(e) => { e.target.style.borderColor = C.gold; }}
              onBlur={(e) => { e.target.style.borderColor = C.sand; }}
            />
            <button
              onClick={send}
              disabled={!input.trim() || loading}
              style={{
                backgroundColor: input.trim() && !loading ? C.gold : C.sand,
                color: input.trim() && !loading ? C.white : '#aaa',
                border: 'none',
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 16,
                cursor: input.trim() && !loading ? 'pointer' : 'default',
                transition: 'background-color 0.15s',
                flexShrink: 0,
              }}
            >
              ➤
            </button>
          </div>

          {/* Footer attribution */}
          <div
            style={{
              textAlign: 'center',
              fontSize: 10,
              color: '#bbb',
              padding: '4px 0 7px',
              backgroundColor: C.white,
              flexShrink: 0,
            }}
          >
            Powered by Claude · Not a substitute for official guidance
          </div>
        </div>
      )}
    </>
  );
}
